/**
 * The demo's GUIDES — the first-run rail and the guided tour — and the one
 * rule between them: one guide on screen at a time.
 *
 * Regression (Oxera, 2026-10-13): the rail's closing panel stayed up while
 * the tour ran — it sat over the Artifacts list the tour's Review step 1 was
 * spotlighting — and on the artifact page the tour card, the rail panel AND
 * the ripple's "What if?" card all showed at once. The two guides are
 * separate portals (both mounted in routes/__root.tsx) and the only thing
 * that ever closed the rail on a tour start was the rail's own button
 * dismissing itself first; the sidebar's "Show me around" starts the same
 * tour and nothing told the rail.
 *
 * So the guides talk:
 *   - a guide that OPENS announces it (`announceGuideOpened`), and every
 *     other guide closes on hearing it (`onGuideOpened`) — whichever button,
 *     sidebar or panel, started it;
 *   - each guide publishes whether it is ON SCREEN (`setGuideOpen`), so
 *     chrome that would otherwise stack on top of a guide — the welcome
 *     banner, the ripple's "What if?" card — can step aside while one is up
 *     (`useGuideOpen`).
 *
 * WHY PRESENCE IS PUBLISHED, NOT DERIVED. The welcome banner already stood
 * down while the arc was PENDING, but a REPLAY is not pending (the seat's
 * seen-mark stands), so the banner stayed up over the replayed welcome state
 * that showed the identical paragraph (filmed on a live demo). Whether a
 * guide is up is component state inside the chrome that mounts it, so the
 * guide itself says so. A module store with `useSyncExternalStore` rather than
 * context, because the two ends live in different trees (the guides portal
 * from __root, the banner and the artifact panel render inside `_app`).
 */

import { useSyncExternalStore } from "react";

export type DemoGuide = "rail" | "tour";

/** Window event carrying `{ guide }` — a guide just came on screen. */
export const GUIDE_OPENED_EVENT = "chaidojo:guide-opened";

interface GuideOpenedDetail {
  guide: DemoGuide;
}

/** Tell every other guide that `guide` just opened. They close. */
export function announceGuideOpened(guide: DemoGuide): void {
  try {
    window.dispatchEvent(
      new CustomEvent<GuideOpenedDetail>(GUIDE_OPENED_EVENT, { detail: { guide } }),
    );
  } catch {
    /* non-DOM context — no other guide to close */
  }
}

/** Hear OTHER guides opening — `self`'s own announcements are filtered out,
 *  so a guide can subscribe and announce without closing itself. */
export function onGuideOpened(self: DemoGuide, handler: (other: DemoGuide) => void): () => void {
  const listener = (e: Event) => {
    const guide = (e as CustomEvent<GuideOpenedDetail>).detail?.guide;
    if (guide && guide !== self) handler(guide);
  };
  window.addEventListener(GUIDE_OPENED_EVENT, listener);
  return () => window.removeEventListener(GUIDE_OPENED_EVENT, listener);
}

// ── presence ────────────────────────────────────────────────────────────────

const open = new Set<DemoGuide>();
const listeners = new Set<() => void>();
/** Snapshots, kept stable between changes for useSyncExternalStore. */
let snapshot = { any: false, rail: false, tour: false };

/** Publish whether `guide` is on screen right now. */
export function setGuideOpen(guide: DemoGuide, isOpen: boolean): void {
  if (open.has(guide) === isOpen) return;
  if (isOpen) open.add(guide);
  else open.delete(guide);
  snapshot = { any: open.size > 0, rail: open.has("rail"), tour: open.has("tour") };
  for (const listener of listeners) listener();
}

/** Is `guide` (or, with no argument, ANY guide) on screen? Non-React read. */
export function isGuideOpen(guide?: DemoGuide): boolean {
  return guide ? snapshot[guide] : snapshot.any;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** True while `guide` — or, with no argument, any guide — is on screen. */
export function useGuideOpen(guide?: DemoGuide): boolean {
  const read = () => isGuideOpen(guide);
  return useSyncExternalStore(subscribe, read, read);
}
